import React from 'react';

import { Control, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Checkbox } from '@/components/ui/checkbox';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { cn } from '@/lib/utils';
import { TimeInput } from '@/modules/common/components/molecules/inputs/time-input';
import { parseTimeToMinuteOfTheDay } from '@/modules/common/components/templates/organization-settings/helpers/parse-work-schedule';
import { DayOfWeek } from '@/types/day-of-week';

import { OrganizationFormValues } from '../../schemas';

interface WorkDayScheduleFieldProps {
  control: Control<OrganizationFormValues>;
  dayOfWeek: DayOfWeek;
  label: string;
}

export const WorkDayScheduleField: React.FC<WorkDayScheduleFieldProps> = ({
  control,
  dayOfWeek,
  label,
}) => {
  const { t } = useTranslation(['authenticated.organization']);
  const dayName = DayOfWeek[dayOfWeek] as keyof typeof DayOfWeek;

  const startTime = useWatch({
    control,
    name: `workSchedule.${dayName}.startTime`,
  });
  const endTime = useWatch({
    control,
    name: `workSchedule.${dayName}.endTime`,
  });

  return (
    <tr>
      <FormField
        control={control}
        name={`workSchedule.${dayName}.enabled`}
        render={({ field }) => {
          // Calculate duration in minutes
          let durationMinutes: number | null = null;
          const start = startTime ? parseTimeToMinuteOfTheDay(startTime) : null;
          const end = endTime ? parseTimeToMinuteOfTheDay(endTime) : null;
          if (start !== null && end !== null) {
            if (end >= start) {
              durationMinutes = end - start;
            } else {
              durationMinutes = 0;
            }
          }

          // Format duration string
          let durationString = '(0h)';
          if (durationMinutes !== null && durationMinutes > 0) {
            const hours = Math.floor(durationMinutes / 60);
            const minutes = durationMinutes % 60;
            durationString = `(${hours}h${minutes ? ` ${minutes}m` : ''})`;
          }

          return (
            <>
              <td className="py-2.5 align-top">
                <FormItem
                  className={cn(
                    'flex flex-row space-x-2 space-y-0 mr-2',
                    !field.value ? 'items-center' : 'items-start'
                  )}
                >
                  <FormControl>
                    <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                  <div className="flex flex-col justify-center">
                    <FormLabel
                      className={cn('text-sm leading-none', !field.value ? ' opacity-70' : '')}
                    >
                      {label}
                    </FormLabel>
                    {field.value && (
                      <span className="mt-0.25 text-xs text-muted-foreground">
                        {durationString}
                      </span>
                    )}
                  </div>
                </FormItem>
              </td>
              <td className="align-middle">
                <div className="flex flex-col">
                  <div className="flex items-center space-x-2">
                    {field.value ? (
                      <>
                        <div className="flex-1">
                          <FormField
                            control={control}
                            name={`workSchedule.${dayName}.startTime` as const}
                            render={({ field }) => (
                              <FormItem>
                                <FormControl>
                                  <TimeInput
                                    value={field.value || ''}
                                    onChange={field.onChange}
                                    placeholder={t(
                                      'authenticated.organization:workingHours.placeholders.startTime'
                                    )}
                                    autoComplete="off"
                                  />
                                </FormControl>
                              </FormItem>
                            )}
                          />
                        </div>
                        <div className="flex h-full w-1.5 items-center">
                          <span className="text-sm font-medium">-</span>
                        </div>
                        <div className="flex-1">
                          <FormField
                            control={control}
                            name={`workSchedule.${dayName}.endTime` as const}
                            render={({ field }) => (
                              <FormItem>
                                <FormControl>
                                  <TimeInput
                                    value={field.value || ''}
                                    onChange={field.onChange}
                                    placeholder={t(
                                      'authenticated.organization:workingHours.placeholders.endTime'
                                    )}
                                    autoComplete="off"
                                  />
                                </FormControl>
                              </FormItem>
                            )}
                          />
                        </div>
                      </>
                    ) : null}
                  </div>
                  {/* Error messages row, does not affect input alignment */}
                  <div className="flex space-x-2">
                    <div className="flex-1">
                      <FormField
                        control={control}
                        name={`workSchedule.${dayName}.startTime` as const}
                        render={() => <FormMessage className="mt-2" />}
                      />
                    </div>
                    <div className="w-1.5" />
                    <div className="flex-1">
                      <FormField
                        control={control}
                        name={`workSchedule.${dayName}.endTime` as const}
                        render={() => <FormMessage className="mt-2" />}
                      />
                    </div>
                  </div>
                </div>
              </td>
            </>
          );
        }}
      />
    </tr>
  );
};
